import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Logger} from '@common-lib';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {ToasterService} from 'angular2-toaster';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {saveAs} from 'file-saver/FileSaver';
import {ConfirmPopupComponent} from '../../components';
import {TranslationsService} from '../../pages-services';

@Component({
    selector: 'app-settings-translations',
    templateUrl: './settings-translations.component.html',
    styleUrls: ['./settings-translations.component.scss']
})
export class SettingsTranslationsComponent implements OnInit {

    countries = [];
    selectedLocale = 'en';
    editModalRef: BsModalRef;
    selectedRow: { key: string, value: string, new: boolean };
    searchTerm = '';
    isExporting = false;
    isUploading = false;
    @ViewChild('editFormTemplate')
    editFormTemplate: TemplateRef<any>;

    tableConfiguration = {
        searchEnabled: false,
        headerEnabled: true,
        orderEnabled: true,
        globalSearchEnabled: false,
        paginationEnabled: true,
        exportEnabled: false,
        clickEvent: false,
        selectRow: false,
        selectCol: false,
        selectCell: false,
        rows: 50,
        additionalActions: false,
        serverPagination: false,
        isLoading: false,
        detailsTemplate: false,
        groupRows: false,
        paginationRangeEnabled: true,
        collapseAllRows: false,
        checkboxes: false
    };

    columns = [
        {key: 'key', title: ' '},
        {key: 'key', title: 'Key'},
        {key: 'value', title: 'Translation'},
        {key: 'actions', title: 'Actions'}
    ];
    data = [];
    rows = [];
    selected = new Set();
    originalRowForEdit: any = null;

    @ViewChild(ConfirmPopupComponent)
    confirmPopup: ConfirmPopupComponent;

    constructor(
        private route: ActivatedRoute,
        private translationsService: TranslationsService,
        private translate: TranslateService,
        private toasterService: ToasterService,
        private modalService: BsModalService,
        private logger: Logger
    ) {
    }

    ngOnInit() {
        this.translationsService.getLocalizations().subscribe((data) => {
            this.countries = data;
            this.getList();
        });
    }

    getList() {
        const locale = this.countries.find((l) => l.language === this.selectedLocale);
        if (locale) {
            this.translationsService.getList(locale.locales[0]).subscribe((data) => {
                this.data = data;
                this.search();
            });
        }
    }

    localeChanged() {
        const locale = this.countries.find((l) => l.language === this.selectedLocale);
        if (locale) {
            this.translationsService.getList(locale.locales[0]).subscribe((data) => {
                this.data = data;
                this.search();
            });
        }
    }


    eventEmitted($event) {
        switch ($event.event) {
            case 'onSelectAll':
                this.data.forEach((val, key) => {
                    if (this.selected.has(key)) {
                        this.selected.delete(key);
                    } else {
                        this.selected.add(key);
                    }
                });
                break;
            case 'onDoubleClick':
                this.editRow($event.value.row);
                break;
        }
    }


    showEditForm(template: TemplateRef<any>) {
        this.editModalRef = this.modalService.show(template);
    }


    editRow(row) {
        this.selectedRow = row;
        this.selectedRow = this.originalRowForEdit = Object.assign({}, row);
        this.editModalRef = this.modalService.show(this.editFormTemplate);
    }

    toggleRow({target: {checked}}, row) {
        const index = this.data.indexOf(row);
        if (checked) {
            this.selected.add(index);
        } else {
            this.selected.delete(index);
        }
    }

    isRowSelected(row) {
        return this.selected.has(this.data.indexOf(row));
    }

    saveTranslation() {
        this.editModalRef.hide();
        const locale = this.countries.find((l) => l.language === this.selectedLocale);
        if (locale) {
            this.translationsService.update(locale.locales[0], this.selectedRow.key, this.selectedRow.value).subscribe(async (result) => {
                this.getList();
                const successMessage = await this.translate.get('webpanel.translations.save.success_message').toPromise();
                this.toasterService.pop('success', '', successMessage);
            });
        }
    }

    search() {
        this.rows = this.data.filter((r) => r.key && r.value && (this.searchTerm.length === 0 ||
            r.key.toLowerCase().indexOf(this.searchTerm.toLowerCase()) !== -1 ||
            r.value.toLowerCase().indexOf(this.searchTerm.toLowerCase()) !== -1));
        return this.rows;
    }

    addNew() {
        this.selectedRow = {
            'key': '',
            'value': '',
            'new': true
        };
        this.editModalRef = this.modalService.show(this.editFormTemplate);
    }

    searchKeyPress(event) {
        if (event.which === 13) {
            this.search();
        }
    }

    async removeItem(item) {
        this.selectedRow = item;
        if (this.selectedRow) {
            this.confirmPopup.show();
        }
    }

    confirm(res) {
        if (res) {
            if (this.selectedRow) {
                this.translationsService.delete(this.selectedRow).subscribe(async (resp) => {
                    this.selectedRow = null;
                    const successMessage = await this.translate.get('webpanel.translations.delete.success_message').toPromise();
                    this.toasterService.pop('success', '', successMessage);
                    this.getList();
                });
            }
        } else {
            this.selectedRow = this.originalRowForEdit;
            this.originalRowForEdit = null;
        }
    }

    decline() {
        this.selectedRow = this.originalRowForEdit;
        this.originalRowForEdit = null;
    }

    saveFile() {
        const locale = this.countries.find((l) => l.language === this.selectedLocale);
        if (locale) {
            this.isExporting = true;
            this.translationsService.getList(locale.locales[0]).subscribe(response => {
                this.saveToFileSystem(response.map((r) => ({key: r.key, value: r.value})).sort((a, b) => a.key.localeCompare(b.key)));
            });
        }

    }

    fileUpload({target}) {
        if (this.isUploading) {
            return;
        }
        this.isUploading = true;
        const fileField = target;
        const file = fileField.files[0];
        const reader = new FileReader();
        reader.onload = () => {
            const result = JSON.parse(reader.result);
            this.translationsService.uploadImport(this.selectedLocale, result).subscribe(async (response) => {
                this.isUploading = false;
                this.getList();
                if (response.success) {
                    let successMessage = await this.translate.get('webpanel.translations.import.success_message').toPromise();
                    this.toasterService.pop('success', '', successMessage);
                } else {
                    let errorMessage = await this.translate.get('webpanel.translations.import.error_message').toPromise();
                    this.toasterService.pop('error', '', errorMessage);
                }
                fileField.value = '';
            });


        };
        reader.readAsText(file);
    }

    private async saveToFileSystem(response) {
        const fileContent = JSON.stringify(response, null, 2);
        const blob = new Blob([fileContent], {type: 'text/plain'});
        saveAs(blob, 'translations-' + this.selectedLocale + '-' + moment().format('YYYYMMDD-HHmm') + '.json');
        this.isExporting = false;
        const successMessage = await this.translate.get('webpanel.translations.export.success_message').toPromise();
        this.toasterService.pop('success', '', successMessage);
    }

}



// WEBPACK FOOTER //
// ./src/app/pages/settings-translations/settings-translations.component.ts